import { useEffect, useState } from 'react';
import mobileQuery from './mobileQuery.json';

/**
 * viewport — the ONE phone/desktop frontier of the house, extracted from
 * MedardExperience on 07/09/2026 so the desktop website (App.js, `PickEntry`)
 * and the guest experience read the same line. Two copies of this query
 * would drift — and the second clause is exactly the kind of detail a copy
 * forgets.
 *
 * A phone counts as mobile in BOTH orientations. A plain « max-width: 767px »
 * broke on rotation: an iPhone turned landscape becomes ~844px WIDE, crossed
 * the line, and swapped the whole tree — which unmounted a PLAYING suite film
 * and threw the guest back to the Médard home (owner report, July 2026). The
 * second clause keeps a landscape phone in the mobile shell, keyed on the
 * SHORT side (≤ 480px — every phone's short side is ≤ ~430px, every tablet's
 * is ≥ 768px), so real tablets and desktops get the wide edition.
 * CSR only (CRA), so reading matchMedia synchronously on first render is safe.
 */
// The query lives in mobileQuery.json so the BUILD can read it too: the
// shell's inline hint (plugins/preload-experience.js) asks the same question
// before main.js runs, and a phone that is one here must be one there.
export const MOBILE_QUERY = mobileQuery.query;
// The complement, for `media=` attributes in the shell (a media query LIST
// cannot be negated as a whole): the screens that get the website. A 768 px
// landscape screen under 481 px tall is a phone held sideways — excluded, as
// MOBILE_QUERY's second clause includes it.
export const SITE_QUERY = mobileQuery.site;

export const matchesMobileViewport = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? Boolean(window.matchMedia(MOBILE_QUERY)?.matches)
        : false;

export function useIsMobile() {
    const [isMobile, setIsMobile] = useState(matchesMobileViewport);
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mql = window.matchMedia(MOBILE_QUERY);
        if (!mql || typeof mql.addEventListener !== 'function') return undefined;
        const onChange = (e) => setIsMobile(e.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);
    return isMobile;
}
